import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { Button, Modal, Table } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import { ImageWithFallback } from "components/lib/ImageWithFallback"
import util from "utils/utils"

import { type IExistingAssessor, type IModalAction, type IPotentialAssessor, TableHeaderItem } from "."

type IAssessorModal = {
  isOpen: boolean
  onClose: () => void
  assessor?: IExistingAssessor | IPotentialAssessor
  existingIdeas?: OpenAPI.Schemas["Idea"][]
  potentialIdeas?: OpenAPI.Schemas["Idea"][]
  getAssessorActions: (
    assessor: IExistingAssessor | IPotentialAssessor,
    idea: OpenAPI.Schemas["Idea"],
  ) => IModalAction[]
}

/*
  This modal can be used for existing/potential assessors but not pending invitations, due to differences in data structures
*/
const AssessorModal = ({
  isOpen,
  onClose,
  assessor,
  existingIdeas = [],
  potentialIdeas = [],
  getAssessorActions,
}: IAssessorModal) => {
  const { t } = useTranslation()

  const IdeaRow = useCallback(
    (idea) => (
      <Table.Row key={idea._id}>
        <Table.Cell>
          <TableHeaderItem as="h4" style={{ fontWeight: "400" }}>
            <ImageWithFallback avatar src={util.ideaCoverImage(idea, "200x200")} fallbackSrc={util.ideaCoverImage()} />
            {idea.name}
          </TableHeaderItem>
        </Table.Cell>
        <Table.Cell collapsing>
          {getAssessorActions(assessor, idea).map((action) => (
            <Button
              key={assessor._id}
              size="tiny"
              content={action.content}
              icon={action.icon}
              loading={action.loading}
              onClick={action.onClick}
            />
          ))}
        </Table.Cell>
      </Table.Row>
    ),
    [assessor, getAssessorActions],
  )

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={isOpen}
      onClose={onClose}
      size="small"
    >
      <Modal.Header>{assessor?.profile.fullName}</Modal.Header>
      <Modal.Content>
        <Table basic="very">
          {potentialIdeas.length && assessor ? (
            <>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Selected local {t("generic.ideas")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{potentialIdeas.map((idea) => IdeaRow(idea))}</Table.Body>
            </>
          ) : null}

          {existingIdeas.length && assessor ? (
            <>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell colSpan="2">Previously assigned {t("generic.ideas")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>{existingIdeas.map((idea) => IdeaRow(idea))}</Table.Body>
            </>
          ) : null}
        </Table>
      </Modal.Content>
      <Modal.Actions>
        <Button content="Done" onClick={onClose} />
      </Modal.Actions>
    </Modal>
  )
}

export default AssessorModal
